// utils/lookoutEnrichment.js
import { API_URL } from '../config/api';

// Resolves everything the Lookout List needs for an order that a caller only
// has the orderNo for (no appointment/production data at hand), by calling
// the two purpose-built endpoints in parallel:
//  - /api/appointments/lookup-for-order: the linked appointment if
//    tbl_Appointments.orderno is filled in, else the customer's closest
//    appointment to the due date (a guess - see appointmentMatchType).
//  - /api/orders/wig-summary: barcode, current room, next rooms, assigned
//    worker - this always resolves for a real order.
//
// Used when bookmarking from the main orders table (OrdersList.jsx), and
// again every time a shared Lookout List item is rendered (live re-resolve,
// not a cached copy - see lookoutShareService.js).
export async function enrichOrderForLookout(orderNo, { customerName, dueDate } = {}) {
  const result = {
    barcode: null,
    currentRoom: null,
    nextRooms: null,
    assignedWorker: null,
    appointmentDate: null,
    meetingWith: null,
    appointmentMatchType: null // 'orderno' (confirmed link) or 'closest-guess' (no link, nearest by date)
  };

  const appointmentParams = new URLSearchParams({ orderNo });
  if (customerName) appointmentParams.append('customerName', customerName);
  if (dueDate) appointmentParams.append('dueDate', dueDate);

  try {
    const [appointmentResult, wigSummaryResult] = await Promise.allSettled([
      fetch(`${API_URL}/api/appointments/lookup-for-order?${appointmentParams.toString()}`),
      fetch(`${API_URL}/api/orders/wig-summary/${orderNo}`)
    ]);

    if (appointmentResult.status === 'fulfilled' && appointmentResult.value.ok) {
      const data = await appointmentResult.value.json();
      if (data.appointment) {
        result.appointmentDate = data.appointment.EventStart;
        result.meetingWith = data.appointment.MeetingWith;
        result.appointmentMatchType = data.matchType;
      }
    }

    if (wigSummaryResult.status === 'fulfilled' && wigSummaryResult.value.ok) {
      const wig = await wigSummaryResult.value.json();
      result.barcode = wig.barcode;
      result.currentRoom = wig.currentRoom;
      result.nextRooms = wig.nextRooms;
      result.assignedWorker = wig.assignedWorker;
    }
  } catch (error) {
    console.error('Error enriching lookout list entry:', error);
  }

  return result;
}
